import type { TestTargetType } from "../../types/TestTarget";
import type { AppTestTarget, DeletionCandidate, MatchResult } from "./types";

/** name 正規化: 前後空白除去のみ */
function normalizeName(name: string): string {
  return (name ?? "").trim();
}

function parentIdOf(t: AppTestTarget): string | undefined {
  return t.parent_test_target_id ?? t.parentId ?? undefined;
}

/** depth: service=0, view/component=1, section/overlay=2, item/component-ref=3 */
function depthOf(type: TestTargetType): number {
  if (type === "service") return 0;
  if (type === "view" || type === "component") return 1;
  if (type === "section" || type === "overlay") return 2;
  return 3; // item, component-ref (参照先 component より後に解決するため同 depth 内で後置)
}

export function matchExistingTargets(
  parsedTargets: AppTestTarget[],
  existingTargets: AppTestTarget[],
): MatchResult {
  /** component-ref の params.referenceTargetId を返す（なければ undefined） */
  const referenceTargetIdOf = (t: AppTestTarget): string | undefined => {
    const p = t.params as Record<string, unknown> | undefined;
    if (!p) return undefined;
    const rid = p["referenceTargetId"];
    return typeof rid === "string" && rid ? rid : undefined;
  };

  /**
   * 照合キー生成:
   * - component-ref: `type|parentId|referenceTargetId` (同一配置先に同一参照は1つ)
   * - その他: `type|name|parentId`
   */
  const matchKey = (t: AppTestTarget, resolvedParent: string | undefined): string => {
    if (t.type === "component-ref") {
      const refId = referenceTargetIdOf(t);
      return `${t.type}|${resolvedParent ?? ""}|${refId ?? ""}`;
    }
    return `${t.type}|${normalizeName(t.name)}|${resolvedParent ?? ""}`;
  };

  // 既存ターゲットを (type, name, 親ID) でインデックス
  const existingByKey = new Map<string, AppTestTarget>();
  for (const t of existingTargets) {
    const key = matchKey(t, parentIdOf(t));
    existingByKey.set(key, t);
  }

  // parsed を depth 順にソート（親が先に解決される）
  const sorted = [...parsedTargets].sort((a, b) => depthOf(a.type) - depthOf(b.type));

  // 旧 ID → 新 ID（既存と照合で書き換えられた ID）のマップ
  const idRemap = new Map<string, string>();

  const resolved: AppTestTarget[] = [];
  const matchedExistingIds = new Set<string>();

  for (const t of sorted) {
    // 親 ID を解決済みに置き換え
    const oldParent = parentIdOf(t);
    const newParent = oldParent ? idRemap.get(oldParent) ?? oldParent : undefined;

    // component-ref の referenceTargetId も idRemap で解決してからキー生成
    const resolvedRefId =
      t.type === "component-ref"
        ? (() => {
            const rid = referenceTargetIdOf(t);
            return rid ? (idRemap.get(rid) ?? rid) : undefined;
          })()
        : undefined;

    const key = matchKey(
      t.type === "component-ref"
        ? // component-ref のキー生成に使う仮オブジェクトに解決済み referenceTargetId を渡す
          ({ ...t, params: { ...(t.params as Record<string, unknown>), referenceTargetId: resolvedRefId } } as AppTestTarget)
        : t,
      newParent,
    );
    const existing = existingByKey.get(key);

    const next: AppTestTarget = {
      ...t,
      parentId: newParent,
      parent_test_target_id: newParent,
      params:
        t.params && typeof t.params === "object"
          ? JSON.parse(JSON.stringify(t.params))
          : t.params,
    };

    if (existing) {
      const oldId = t.test_target_id ?? t.id;
      next.test_target_id = existing.test_target_id ?? existing.id;
      next.id = existing.id;
      (next as any).operation = "UPDATE";
      // parsed の元 id は client UUID、参照置換のため remap に登録
      idRemap.set(oldId, existing.test_target_id ?? existing.id);
      idRemap.set(t.id, existing.id);
      matchedExistingIds.add(existing.test_target_id ?? existing.id);
    } else {
      (next as any).operation = "CREATE";
      // 新規。既存と紛らわしくない client UUID をそのまま使う
      idRemap.set(t.test_target_id ?? t.id, t.test_target_id ?? t.id);
      idRemap.set(t.id, t.id);
    }
    resolved.push(next);
  }

  // 仕様書に登場しない既存ターゲットを集計し、削除候補として本体を収集
  const unmatched: Partial<Record<TestTargetType, number>> = {};
  const deletionCandidates: DeletionCandidate[] = [];
  for (const t of existingTargets) {
    if (matchedExistingIds.has(t.test_target_id ?? t.id)) continue;
    unmatched[t.type] = (unmatched[t.type] ?? 0) + 1;
    deletionCandidates.push({
      id: t.test_target_id ?? t.id,
      name: t.name,
      type: t.type,
      parentId: t.parent_test_target_id ?? t.parentId ?? undefined,
    });
  }

  // 解決済み id を使って transitions[].targetId / componentConditions[].componentId / component_ids[] / referenceTargetId も書き換え
  for (const t of resolved) {
    const params = t.params as Record<string, unknown> | undefined;
    if (!params) continue;
    if (Array.isArray(params.component_ids)) {
      params.component_ids = (params.component_ids as string[]).map((cid) => idRemap.get(cid) ?? cid);
    }
    if (Array.isArray(params.componentConditions)) {
      params.componentConditions = (
        params.componentConditions as { componentId: string; conditions: string[] }[]
      ).map((c) => ({
        componentId: idRemap.get(c.componentId) ?? c.componentId,
        conditions: c.conditions,
      }));
    }
    if (Array.isArray(params.transitions)) {
      params.transitions = (
        params.transitions as { targetId: string; transitionType?: string; condition?: string }[]
      ).map((tr) => ({
        ...tr,
        targetId: tr.targetId ? idRemap.get(tr.targetId) ?? tr.targetId : tr.targetId,
      }));
    }
    // referenceTargetId (component-ref) — idRemap で解決済み ID に書き換え
    if (typeof params.referenceTargetId === "string" && params.referenceTargetId) {
      params.referenceTargetId = idRemap.get(params.referenceTargetId) ?? params.referenceTargetId;
    }
  }

  return {
    resolvedTargets: resolved,
    matchedExistingIds,
    unmatchedExistingCount: unmatched,
    deletionCandidates,
  };
}
